import { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { useHistory, useParams } from 'react-router-dom';
import { createBillingRate } from '../../../api/services/billingRateService';
import { ResponseStatus } from '../../../components/notifications';
import Layout from '../../../components/layout';
import SubmitButton from '../../../components/submitButton';
import LayoutError from '../../../components/layoutError';
import { getDisciplines } from '../../../api/services/disciplineService';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import Discipline from '../../../api/models/discipline';
import { getDistrict } from '../../../api/services/districtService';
import Program from '../../../api/models/program';

type Params = {
  districtId: string;
};

const fetchDistrict = async ({ districtId }: any) => {
  return await getDistrict(districtId);
};

const CreateBillingRates = () => {
  const history = useHistory();
  const { districtId } = useParams<Params>();
  const [disciplineId, setDisciplineId] = useState<number>(0);
  const [programId, setProgramId] = useState<number>(-1);
  const [programs, setPrograms] = useState<Program[]>();
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  const [rate, setRate] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();

  const disciplinesData = useAsync({
    promiseFn: getDisciplines,
    onResolve: (response) => {
      const disciplines: Discipline[] = response.data.disciplines;
      setDisciplines(disciplines);

    }
  });

  useAsync({
    promiseFn: fetchDistrict,
    districtId,
    onResolve: (r) => {
      setPrograms(r.data.district.programs);
    },
  });

  const saveBillingRate = async () => {
    return await createBillingRate({
      billingRateId: 0,
      disciplineId: disciplineId,
      programId: (programId !== -1) ? programId : undefined,
      districtId: Number(districtId),
      rate: rate,
      startDate: startDate!.toLocaleDateString(),
      endDate: endDate!.toLocaleDateString(),
      isVisible: true
    });
  };

  const save = useAsync({
    deferFn: saveBillingRate,
    onResolve: () => {
      history.push(`/admin/districts/${districtId}`, {
        message: `Billing rate added successfully!`,
      });
    },
  });

  // Error handling
  if (disciplinesData.error) return <LayoutError />;

  return (
    <Layout isLoading={
      save.isPending ||
      disciplinesData.isPending
    }>
      <Fragment>
        <ResponseStatus state={save} />
        <div className='grid grid-cols-6 gap-6 mb-6'>
          <div className='col-span-6 sm:col-span-6'>
            <label className='mb-2'>Discipline:<span className='pl-1 text-red-500'>*</span></label>
            <select
              className='w-full'
              defaultValue={disciplineId}
              onChange={(e) => setDisciplineId(Number.parseInt(e.target.value))}
            >
              <option key="discipline_default" value={-1}>Select Discipline</option>
              {disciplines.sort((a, b) => {
                return a.name.localeCompare(b.name);
              }).map((d, i) => (
                <option key={`discipline_${d.disciplineId}`} value={d.disciplineId}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <label className='mb-2'>Program:</label>
            <select
              className='w-full'
              defaultValue={programId}
              onChange={(e) => setProgramId(Number.parseInt(e.target.value))}
            >
              <option key="program_default" value={-1}>Select Program</option>
              {programs?.sort((a, b) => {
                return a.name.localeCompare(b.name);
              }).map((d, i) => (
                <option key={`program_${d.programId}`} value={d.programId}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
          <div className='col-span-6 sm:col-span-6 rate-field'>
            <div className='text-sm'>
              <label htmlFor='rate' className='primary'>
                Rate<span className='pl-1 text-red-500'>*</span>
              </label>
            </div>
            <input
              type="number"
              name='rate'
              min='0'
              className='w-full'
              onChange={(e) => setRate(Number(e.target.value))}
              value={rate.toString()}
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <label className='mb-2'>Start Date:<span className='pl-1 text-red-500'>*</span></label>
            <DatePicker
              name='rate'
              selected={startDate/*moment(startDate).toDate()*/}
              onChange={(date: Date) => setStartDate(date!)}
              dateFormat="MM/dd/yyyy"
            />
          </div>
          <div className='col-span-6 sm:col-span-6'>
            <label className='mb-2'>End Date:<span className='pl-1 text-red-500'>*</span></label>
            <DatePicker
              name='rate'
              selected={endDate}
              minDate={moment(startDate).toDate()}
              onChange={(date: Date) => setEndDate(date!)}
              dateFormat="MM/dd/yyyy"
            />
          </div>
        </div>
        <div className='text-right'>
          <SubmitButton
            text='Create Billing Rate'
            spinning={save.isPending}
            disabled={rate < 0 || disciplineId === 0 || disciplineId === -1 || startDate === undefined || endDate === undefined || startDate === null || endDate === null}
            onClick={() => {
              if (rate <= 0) {
                alert("Rate must be greater than 0 to proceed.");
                return;
              }
              save.run();
            }}
          />
        </div>
      </Fragment>
    </Layout>
  );
};

export default CreateBillingRates;
